import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFailedSubmissions, type Submission } from '../api/leads';
import { PageContainer } from '../components/layout/PageContainer';
import { Card } from '../components/ui/Card';
import { StatusBadge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { getQueueStats, type QueueStatsResponse } from '../api/queue';
import { getQAQueue, type InternalQAQueueResponse } from '../api/internal-qa';
import { formatRelativeTime, formatDuration } from '../utils/formatters';

interface StatsCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: 'blue' | 'yellow' | 'green' | 'red';
  linkTo?: string;
}

function StatsCard({ title, value, icon, color, linkTo }: StatsCardProps) {
  const colorStyles = {
    blue: 'bg-blue-50 text-blue-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
  };

  const content = (
    <Card className="hover:shadow-md transition-shadow">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colorStyles[color]}`}>{icon}</div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </Card>
  );

  if (linkTo) {
    return <Link to={linkTo}>{content}</Link>;
  }
  return content;
}

export function DashboardPage() {
  const [queueStats, setQueueStats] = useState<QueueStatsResponse | null>(null);
  const [qaQueue, setQAQueue] = useState<InternalQAQueueResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Undelivered enquiries across every site. Loaded separately and failure is
  // swallowed: this is an extra warning surface, and it must never be the
  // reason the dashboard fails to render.
  const [undelivered, setUndelivered] = useState<Submission[]>([]);

  useEffect(() => {
    async function fetchData() {
      try {
        const [statsData, qaData] = await Promise.all([
          getQueueStats(),
          getQAQueue(),
        ]);
        setQueueStats(statsData);
        setQAQueue(qaData);

        try {
          const failed = await getFailedSubmissions();
          setUndelivered(failed.data.submissions);
        } catch (leadErr) {
          console.error('Could not load undelivered enquiries:', leadErr);
        }
      } catch (err) {
        setError('Failed to load dashboard data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  if (loading) {
    return (
      <PageContainer title="Dashboard">
        <div className="flex items-center justify-center min-h-[400px]">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer title="Dashboard">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg">{error}</div>
      </PageContainer>
    );
  }

  // Calculate stats
  const pendingQA = qaQueue?.queue.filter(
    (s) => s.status === 'pending-internal-qa'
  ).length || 0;
  const needsRevision = qaQueue?.queue.filter(
    (s) => s.status === 'needs-internal-revision'
  ).length || 0;
  const generating =
    queueStats?.stats.find((s) => s.status === 'generating')?.count || 0;
  const queued =
    queueStats?.stats.find((s) => s.status === 'queued')?.count || 0;

  return (
    <PageContainer
      title="Dashboard"
      subtitle="Overview of your site generation pipeline"
    >
      {/*
        * Undelivered enquiries.
        *
        * The whole reason this exists: a submission is stored before it is
        * emailed, so a delivery failure is recorded faithfully — and used to be
        * seen by nobody. A per-site tab still requires someone to go looking at
        * the right site. This is the part that speaks up unprompted.
        */}
      {undelivered.length > 0 && (
        <div className="mb-8 rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div className="min-w-0">
              <h3 className="font-semibold text-red-900">
                {undelivered.length} enquir{undelivered.length === 1 ? 'y was' : 'ies were'} not delivered
              </h3>
              <p className="text-sm text-red-800 mt-1">
                The {undelivered.length === 1 ? 'enquiry is' : 'enquiries are'} safe — only the email
                failed. Open the site's Leads tab to read {undelivered.length === 1 ? 'it' : 'them'} and resend.
              </p>
              <ul className="mt-3 space-y-1">
                {undelivered.slice(0, 5).map((s) => (
                  <li key={s.id} className="text-sm">
                    <Link
                      to={`/internal-qa/${s.website_id}`}
                      className="text-red-900 underline hover:no-underline"
                    >
                      {s.business_name || s.website_id}
                    </Link>
                    <span className="text-red-800"> — {s.name} &lt;{s.email}&gt;</span>
                  </li>
                ))}
              </ul>
              {undelivered.length > 5 && (
                <p className="text-xs text-red-700 mt-2">and {undelivered.length - 5} more</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatsCard
          title="Pending QA Review"
          value={pendingQA}
          color="yellow"
          linkTo="/sites?status=pending-internal-qa"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <StatsCard
          title="Needs Revision"
          value={needsRevision}
          color="red"
          linkTo="/sites?status=needs-internal-revision"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          }
        />
        <StatsCard
          title="Generating"
          value={generating}
          color="blue"
          linkTo="/queue"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />
        <StatsCard
          title="Queued"
          value={queued}
          color="green"
          linkTo="/queue"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
          }
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Needs Attention */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Needs Attention
            </h2>
            <Link
              to="/sites?status=pending-internal-qa"
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              View all →
            </Link>
          </div>
          {qaQueue && qaQueue.queue.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {qaQueue.queue.slice(0, 5).map((site) => (
                <li key={site.website_id}>
                  <Link
                    to={`/internal-qa/${site.website_id}`}
                    className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-4 px-4 rounded"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {site.business_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatRelativeTime(site.created_at)}
                      </p>
                    </div>
                    <StatusBadge status={site.status} />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-center py-8">
              No sites need attention right now
            </p>
          )}
        </Card>

        {/* Recent Completions */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Completions
            </h2>
            <Link
              to="/sites"
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              View all →
            </Link>
          </div>
          {queueStats && queueStats.recent_completed.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {queueStats.recent_completed.slice(0, 5).map((job) => (
                <li key={job.id}>
                  <Link
                    to={`/internal-qa/${job.website_id}`}
                    className="flex items-center justify-between py-3 hover:bg-gray-50 -mx-4 px-4 rounded"
                  >
                    <div>
                      <p className="font-medium text-gray-900">
                        {job.business_name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatRelativeTime(job.completed_at)}
                      </p>
                    </div>
                    {job.generation_time_seconds && (
                      <span className="text-sm text-gray-400">
                        {formatDuration(job.generation_time_seconds)}
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500 text-center py-8">
              No recent completions
            </p>
          )}
        </Card>
      </div>
    </PageContainer>
  );
}
